import { Link, Outlet } from "react-router";
import { FaGithub, FaLinkedin, FaGoogleScholar } from "react-icons/fa6";
import { MdNotifications, MdOutlineEdit, MdSettings } from "react-icons/md";
import { useIntl, FormattedMessage, FormattedDate } from "react-intl";
import ErrorPage from "@/pages/_error";
import EntityProfilePic from "@/components/EntityProfilePic";
import NavItem from "@/components/NavItem";
import { Dropdown } from "@/components/Dropdown";
import { ToolbarButton } from "@/components/Button";
import { ScrollableScaffoldNav } from "@/components/ScrollableScaffoldNav";
import EntityHelmet from "@/components/EntityHelmet";
import OrcidId from "@/components/OrcidId";
import { Container } from "@/components/Container";
import { HeaderContainer } from "@/components/HeaderContainer";
import { graphql, Environment } from "react-relay";
import { LoaderArgs } from "@/utils/route";
import { EntityScaffoldQuery as EntityScaffoldQueryType } from "./__generated__/EntityScaffoldQuery.graphql";
import { preload, usePreloaded } from "@/utils/relay";
export default function EntityScaffold() {
  const intl = useIntl();
  const {
    variables,
    query: { entityByUsername: entity, viewer },
  } = useEntityScaffoldQuery();
  if (!entity) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage(
          { defaultMessage: "@{username} not found" },
          { username: variables.username },
        )}
      />
    );
  }
  return (
    <>
      <HeaderContainer>
        <EntityHelmet entity={entity} />
        <div className="space-y-6">
          <div className="flex flex-row space-x-6 items-center">
            <div className="pr-6">
              <EntityProfilePic entity={entity} size="28" />
            </div>
            <div className="grow inline-flex flex-col">
              <h1 className="text-lg font-bold font-poppins inline-flex items-center gap-2">
                {entity.displayName}
                {entity.orcid && (
                  <OrcidId orcid={entity.orcid} variant="icon" />
                )}
              </h1>
              {entity.jobTitle ||
                (entity.organization && (
                  <h2>
                    {entity.jobTitle}
                    {entity.jobTitle && entity.organization && " | "}
                    {entity.organization}
                  </h2>
                ))}
              {entity.location && <h2>{entity.location}</h2>}
              {entity.createdAt && (
                <h3>
                  <FormattedMessage defaultMessage="Joined" />{" "}
                  <FormattedDate
                    value={entity.createdAt}
                    month="long"
                    day="numeric"
                    year="numeric"
                    timeZone="UTC"
                  />
                </h3>
              )}
              {typeof entity.karma !== "undefined" && (
                <p>
                  <FormattedMessage defaultMessage="Karma" />{" "}
                  <span className="font-mono">{entity.karma}</span>
                </p>
              )}
              <div className="flex space-x-2 items-center">
                {entity.github && (
                  <Link
                    to={`https://github.com/${entity.github}`}
                    target="_blank"
                  >
                    {<FaGithub />}
                  </Link>
                )}
                {entity.linkedin && (
                  <Link
                    to={`https://linkedin.com/${entity.linkedin}`}
                    target="_blank"
                  >
                    {
                      <FaLinkedin
                        title={intl.formatMessage(
                          { defaultMessage: "Linkedin profile of {username}" },
                          { username: entity.username },
                        )}
                      />
                    }
                  </Link>
                )}
                {entity.googleScholar && (
                  <Link
                    to={`https://scholar.google.com/citations?&user=${encodeURIComponent(entity.googleScholar)}`}
                    target="_blank"
                  >
                    {<FaGoogleScholar />}
                  </Link>
                )}
                {entity.orcid && (
                  <OrcidId orcid={entity.orcid} className="text-sm" />
                )}
                {entity.website && (
                  <Link to={entity.website} target="_blank">
                    {entity.website}
                  </Link>
                )}
              </div>
            </div>
            {viewer?.id != entity.id && entity.viewerCanEdit && (
              <SettingsButton
                username={entity.username}
                canConfigureNotifications={
                  !!entity.viewerCanConfigureNotifications
                }
              />
            )}
          </div>
          <div>
            <ScrollableScaffoldNav>
              <NavItem to={`/${entity.username}`} end variant="secondary">
                <FormattedMessage defaultMessage="Overview" />
              </NavItem>
              <NavItem
                to={`/${entity.username}/submissions`}
                variant="secondary"
              >
                <FormattedMessage defaultMessage="Last submissions" />
              </NavItem>
              <NavItem to={`/${entity.username}/datasets`} variant="secondary">
                <FormattedMessage defaultMessage="Datasets" />
              </NavItem>
              <NavItem
                to={`/${entity.username}/workspaces`}
                variant="secondary"
              >
                <FormattedMessage defaultMessage="Workspaces" />
              </NavItem>
              {(entity.orcid || !!entity.linkedPapers?.totalCount) && (
                <NavItem to={`/${entity.username}/papers`} variant="secondary">
                  <FormattedMessage defaultMessage="Papers" />
                </NavItem>
              )}
              {entity.kind == "USER" && (
                <NavItem
                  to={`/${entity.username}/comments`}
                  variant="secondary"
                >
                  <FormattedMessage defaultMessage="Last comments" />
                </NavItem>
              )}
              {entity.kind == "USER" && (
                <NavItem to={`/${entity.username}/topics`} variant="secondary">
                  <FormattedMessage defaultMessage="Last topics" />
                </NavItem>
              )}
              {entity.kind == "USER" && (
                <NavItem
                  to={`/${entity.username}/organizations`}
                  variant="secondary"
                >
                  <FormattedMessage defaultMessage="Organizations" />
                </NavItem>
              )}
              {entity.kind == "ORGANIZATION" && (
                <NavItem to={`/${entity.username}/users`} variant="secondary">
                  <FormattedMessage defaultMessage="Users" />
                </NavItem>
              )}
            </ScrollableScaffoldNav>
          </div>
        </div>
      </HeaderContainer>
      <Container>
        <div className="pt-5 pb-8">
          <Outlet />
        </div>
      </Container>
    </>
  );
}
interface SettingsButtonProps {
  username: string;
  canConfigureNotifications: boolean;
}
const SettingsButton = ({
  username,
  canConfigureNotifications,
}: SettingsButtonProps) => (
  <Dropdown
    trigger={
      <ToolbarButton>
        <MdSettings />
      </ToolbarButton>
    }
  >
    <Dropdown.List header={<FormattedMessage defaultMessage="Settings" />}>
      <Link to={`/${username}/edit`}>
        <Dropdown.Item icon={<MdOutlineEdit />}>
          <FormattedMessage defaultMessage="Edit Profile" />
        </Dropdown.Item>
      </Link>

      {canConfigureNotifications && (
        <Link to="/settings/notifications">
          <Dropdown.Item icon={<MdNotifications />}>
            <FormattedMessage defaultMessage="Notifications" />
          </Dropdown.Item>
        </Link>
      )}
    </Dropdown.List>
  </Dropdown>
);
const EntityScaffoldQuery = graphql`
  query EntityScaffoldQuery($username: String!) {
    viewer @ifAllowed {
      id
    }
    entityByUsername(username: $username) {
      id
      kind
      displayName
      location
      username
      linkedin
      github
      googleScholar
      website
      bio
      createdAt
      imageThumbnail
      ...EntityProfilePicFragment @arguments(thumbnail: false)
      ... on User {
        viewerCanEdit: viewerCan(action: UPDATE_USER)
        viewerCanConfigureNotifications: viewerCan(action: UPDATE_USER)
        organization
        jobTitle
        karma
        orcid
        linkedPapers: papers(first: 0) {
          totalCount
        }
      }
      ... on Organization {
        viewerCanEdit: viewerCan(action: UPDATE_ORGANIZATION)
      }
      ...EntityHelmetFragment
    }
  }
`;
export const loader = (environment: Environment) => {
  return ({ params: { username } }: LoaderArgs) => {
    if (!username) {
      throw new Response("No username", { status: 500 });
    }
    return preload<EntityScaffoldQueryType>(environment, EntityScaffoldQuery, {
      username,
    });
  };
};
const useEntityScaffoldQuery = () => usePreloaded<EntityScaffoldQueryType>();
